/**
 * Timetable Studio — season workbook export (Chris, 2026-09-02).
 *
 * One workbook:
 *   1. "Summary"            — the Season-by-Month table, OPTIMISED then
 *                             UNOPTIMISED, with season totals (same yearTotals
 *                             arithmetic as the screen and the board paper).
 *   2. One sheet per month  — the Weekly Service Schedule day by day:
 *                             optimised block then unoptimised block, each with
 *                             services / sets / passengers / revenue / costs /
 *                             profit per DAY and a month total. The unoptimised
 *                             side names the season its ACTUAL sales come from.
 *   3. One "<Month> TT" sheet per month — the timetable for each running
 *                             pattern: optimised then unoptimised, with
 *                             departure, from, to, arrival, capacity, set and
 *                             loco. Pullman dining workings are listed in both
 *                             (they traffic through the designed timetable).
 *   4. "Fleet" (Chris, 2026-09-11) — the rolling-stock register with every
 *                             contract field, the availability grid (days out
 *                             per engine per running week, fewest available),
 *                             each engine's season on the roster (minimum,
 *                             miles rostered, days, blocks), and the ALLOCATION
 *                             — every date's turns and the engine each got, why,
 *                             swapped / pinned / failure.
 *   5. "Cost model"          — the Management P&L lines as the Cost model stage
 *                             shows them, source and modelled by month, the
 *                             rolling-stock lines (named engines, Fixed Rolling
 *                             Stock, Variable after Fixed) included.
 *
 * exceljs is ~1MB — dynamically imported on use (repo convention,
 * researchXlsx.ts). Downloads via downloadBlob — never a popup (law 7).
 */
import type ExcelJS from 'exceljs' // types only — the library is dynamically imported on use
import { downloadBlob } from '../researchShared'
import type { YearRow } from './YearComparePanel'
import { yearTotals, monthFigures } from './YearComparePanel'
import type { ExecDaySide } from './execPdf'

// ---------------------------------------------------------------------------
//  The data contract (assembled by buildSeasonExec in ResearchStudio).
// ---------------------------------------------------------------------------
export interface StudioXlsxTtRow {
  time: string; from: string; to: string; arr: string
  capacity: number; set: string; loco: string
  kind: 'Service' | 'ECS' | 'Pullman'
}
export interface StudioXlsxPlan {
  label: string
  colour: string
  dates: string[]
  opt: StudioXlsxTtRow[]
  cur: StudioXlsxTtRow[]
}
export interface StudioXlsxDay {
  date: string   // ISO
  dow: number    // 0 = Sunday
  colour: string
  dayClass: string
  opt: ExecDaySide | null
  cur: ExecDaySide | null
  pullman: { services: number; covers: number; net: number } | null
}
export interface StudioXlsxMonth {
  key: string
  label: string
  days: StudioXlsxDay[]
  plans: StudioXlsxPlan[]
  /** Which season's actual sales price the unoptimised side. */
  basis: string
  /** Days with no like-for-like basis day (shown at the day-type average). */
  unmatched: number
  /** Days whose like-for-like basis day ran NO standard services (0 services,
   *  fixed cost only, both sides). */
  noService: number
  /** Pullman dining this month: SERVICES (one = out and back), the route, and
   *  crossing conflicts with the design. */
  pullman: { workings: number; conflicts: number; description?: string } | null
  /** Highest number of locos hauling PASSENGER services on a single day this
   *  month (diesel ECS positioning turns are not passenger service). */
  maxLocos: { opt: number; cur: number }
}
/** The Fleet sheet's data (assembled by the orchestrator from the register, fleetAvailability.ts and engineRoster.ts). */
export interface StudioXlsxFleet {
  register: {
    number: string; name: string; owner: string; ownership: string; traction: string; fuel: string; whitby: boolean; inSimulation: boolean
    costPerMile: number | null; dayRate: number | null; fixedAnnualCost: number | null; maintenancePerMile: number | null
    minContractMiles: number | null; basisMiles: number | null; basisAssumed: string
    activeFrom: string | null; activeTo: string | null; washoutSeed: string | null; washoutCycleDays: number | null; washoutLockoutDays: number | null
    boilerCertExpiry: string | null; unavailable: string; notes: string
  }[]
  /** The availability grid over the season's running weeks. */
  weeks: string[]
  outDays: { engine: string; cells: number[]; reasons: string[][] }[]
  available: { traction: string; cells: number[] }[]
  /** Each engine's season on the roster (null without a roster). */
  engines: { engine: string; traction: string; minimum: number | null; miles: number; days: number; blocks: number; milesByMonth: Record<number, number> }[] | null
  /** Every date's turns and the engine each got. */
  allocation: { date: string; colour: string; turn: string; traction: string; engine: string; reason: string; swapped: boolean; pinned: boolean; failure: boolean; miles: number }[]
  monthNos: number[]
}
/** The Cost model sheet's data — the same lines the Cost model stage shows (costLines.ts::buildSim). */
export interface StudioXlsxCostLine {
  label: string; category: string; kind: string; behaviour: string; rate: string | null; included: boolean
  basisMonths: number[]; modelMonths: number[]; basisTotal: number; modelTotal: number; delta: number
  rollingStock: boolean; memo: boolean
}
export interface StudioXlsxInput {
  season: string
  yearRows: YearRow[]
  months: StudioXlsxMonth[]
  fleet?: StudioXlsxFleet | null
  costModel?: { basis: string; hasModel: boolean; lines: StudioXlsxCostLine[] } | null
}

// ---------------------------------------------------------------------------
//  Styling (researchXlsx conventions; ASCII-only number formats — a non-ASCII
//  minus in a numFmt makes Excel offer to "repair" the file).
// ---------------------------------------------------------------------------
const HEAD_BG = 'FF0B3D2E'
const SUB_BG = 'FFEDE7D8'
const BLOCK_BG = 'FFF7F3EA'
const MUTED = 'FF6B6B6B'
const MONEY = '£#,##0'
const INT = '#,##0'
const DOWS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const setCols = (ws: ExcelJS.Worksheet, widths: number[]) => { ws.columns = widths.map((w) => ({ width: w })) }

const titleRow = (ws: ExcelJS.Worksheet, text: string, span: number) => {
  const r = ws.addRow([text])
  ws.mergeCells(r.number, 1, r.number, span)
  r.getCell(1).font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } }
  r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEAD_BG } }
  r.height = 20
}
const noteRow = (ws: ExcelJS.Worksheet, text: string, span: number) => {
  const r = ws.addRow([text])
  ws.mergeCells(r.number, 1, r.number, span)
  r.getCell(1).font = { italic: true, size: 9, color: { argb: MUTED } }
  r.getCell(1).alignment = { wrapText: true, vertical: 'top' }
  r.height = 26
}
const headerRow = (ws: ExcelJS.Worksheet, cells: string[], rightFrom = 3) => {
  const r = ws.addRow(cells)
  r.eachCell((c, i) => {
    c.font = { bold: true, size: 9.5 }
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: SUB_BG } }
    c.border = { bottom: { style: 'thin', color: { argb: 'FFB0A89A' } } }
    if (i >= rightFrom) c.alignment = { horizontal: 'right' }
  })
}
const blockRow = (ws: ExcelJS.Worksheet, text: string, span: number) => {
  const r = ws.addRow([text])
  ws.mergeCells(r.number, 1, r.number, span)
  r.getCell(1).font = { bold: true, size: 10.5 }
  r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLOCK_BG } }
}
const fmt = (r: ExcelJS.Row, moneyCols: number[], intCols: number[]) => {
  for (const c of moneyCols) r.getCell(c).numFmt = MONEY
  for (const c of intCols) r.getCell(c).numFmt = INT
}
const boldTotal = (r: ExcelJS.Row) => { r.eachCell((c) => { c.font = { bold: true, size: 9.5 } }); r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLOCK_BG } } }

// ---------------------------------------------------------------------------
//  Composition — pure, so it can be exercised headlessly.
// ---------------------------------------------------------------------------
export async function composeStudioWorkbook(input: StudioXlsxInput): Promise<ExcelJS.Workbook> {
  const ExcelJSlib = (await import('exceljs')).default
  const wb = new ExcelJSlib.Workbook()
  wb.creator = 'NYMR Reports — Timetable Studio'

  // ---- 1. Summary: Season by Month, optimised then unoptimised -------------
  const sum = wb.addWorksheet('Summary')
  setCols(sum, [14, 9, 10, 10, 12, 12, 13, 13, 13, 13, 13])
  titleRow(sum, `Timetable Studio — ${input.season} season by month`, 11)
  noteRow(sum, 'Every month of the season is listed, running or not, and every month is the SUM of its dates on the month sheets (day for day, the same cells), so this table always adds up to them. Passengers are admissions — people, counted once — on both sides. Turned away = passengers turned away by timing: people in the like day\'s demand who did not travel on that side\'s timetable — no train within 75 minutes of their time once the Recapture discount applies, or the nearest train had no way home; they are not in Passengers (a month showing — was saved before this figure existed). FIXED is the P&L\'s fixed lines for THAT month: the line runs all year, so a month with no standard services carries them with nothing else (the Cost model sliders are how they flex). VARIABLE is the cost of running the trains: per running day, per turn, per train-mile, per passenger. A date whose like-for-like day ran no standard services carries 0 services and its fixed share only. Max Locos is the highest number of locomotives hauling PASSENGER services on a single day that month — diesel ECS positioning turns are not counted. The money matches the season table on screen and in the board paper.', 10)
  const tot = yearTotals(input.yearRows)
  // Max locos in PASSENGER service, per month, keyed by "<MonthShort> <year>"
  // label — computed from the engine diagrams, not the fleet-cost maxima.
  const maxLocosByLabel = new Map(input.months.map((m) => [m.label, m.maxLocos]))
  const sideBlock = (which: 'mod' | 'cur', label: string) => {
    blockRow(sum, label, 11)
    headerRow(sum, ['Month', 'Days', 'Turns/day', 'Max Locos', 'Passengers', 'Turned away', 'Revenue', 'Fixed', 'Variable', 'Costs', 'Profit'])
    let seasonMaxLocos = 0
    for (const r of input.yearRows) {
      const s = which === 'mod' ? r.mod : r.cur
      const days = r.runningDays ?? 0
      const f = monthFigures(s, r.runningDays)
      const mx = [...maxLocosByLabel.entries()].find(([k]) => k.startsWith(r.monthLabel.slice(0, 3)))?.[1]
      const locos = mx ? (which === 'mod' ? mx.opt : mx.cur) : 0
      seasonMaxLocos = Math.max(seasonMaxLocos, locos)
      const runs = !!s && s.services > 0
      const row = sum.addRow(s && f
        ? [r.monthLabel, days,
            runs ? (s.servicesRange ? `${s.servicesRange[0]}–${s.servicesRange[1]}` : String(s.services)) : (days === 0 ? 'no standard services' : '0'),
            runs && locos > 0 ? locos : '—',
            Math.round(f.pax), f.turnedAway == null ? '—' : Math.round(f.turnedAway), f.revenue, f.fixed ?? '—', f.variable ?? '—', f.costs, f.profit]
        : [r.monthLabel, days, '—', '—', '—', '—', '—', '—', '—', '—', '—'])
      fmt(row, [7, 8, 9, 10, 11], [2, 4, 5, 6])
    }
    const t = which === 'mod' ? tot.mod : tot.cur
    const trow = sum.addRow(t
      ? ['SEASON', tot.runningDays, '', seasonMaxLocos > 0 ? seasonMaxLocos : '—', Math.round(t.carried), t.turnedAway == null ? '—' : Math.round(t.turnedAway), t.revenue, t.fixed ?? '—', t.variable ?? '—', t.costs, t.profit]
      : ['SEASON', tot.runningDays, '', '—', '—', '—', '—', '—', '—', '—', '—'])
    fmt(trow, [7, 8, 9, 10, 11], [2, 4, 5, 6])
    boldTotal(trow)
    sum.addRow([])
  }
  sideBlock('mod', 'OPTIMISED — the designed season')
  sideBlock('cur', 'UNOPTIMISED — the timetable as run today')
  sum.views = [{ state: 'frozen', ySplit: 2 }]

  // ---- 2. One WSS sheet per month ------------------------------------------
  for (const m of input.months) {
    const ws = wb.addWorksheet(m.label.slice(0, 31))
    setCols(ws, [12, 11, 9, 9, 7, 12, 12, 12, 12, 13, 15, 34])
    titleRow(ws, `Weekly Service Schedule — ${m.label}`, 12)
    noteRow(ws, `Day for day: Optimised = the designed timetable valued on each date's like-for-like basis day's ACTUAL demand. Unoptimised = the timetable as run today; passengers and takings are that day's ACTUAL sales from ${m.basis || 'the sales basis season'}${m.unmatched > 0 ? ` (${m.unmatched} day${m.unmatched === 1 ? '' : 's'} had no like-for-like basis day and show the day-type average)` : ''}. Passengers are admissions (people, counted once). "No standard services" = the like day sold no timed departure (a gala, an event, or not running): 0 services, £0, fixed cost only, both sides${m.noService > 0 ? ` (${m.noService} such day${m.noService === 1 ? '' : 's'} this month)` : ''}. Pullman covers = dining covers (people) on the Pullman that day; Pullman revenue = its net £ — both OUTSIDE the daily-services P&L.${m.pullman ? ` Pullman this month: ${m.pullman.workings} service${m.pullman.workings === 1 ? '' : 's'} (${m.pullman.description ?? 'out and back'}), ${m.pullman.conflicts === 0 ? 'no crossing conflicts with the design' : `${m.pullman.conflicts} crossing conflict(s) with the design — adjust`}.` : ''}`, 12)
    const daysBlock = (which: 'opt' | 'cur', label: string) => {
      blockRow(ws, label, 12)
      headerRow(ws, ['Date', 'Day', 'Colour', 'Services', 'Sets', 'Passengers', 'Revenue', 'Costs', 'Profit', 'Pullman covers', 'Pullman revenue £', 'Basis'], 4)
      const t = { services: 0, pax: 0, revenue: 0, costs: 0, profit: 0, pullman: 0, pullmanNet: 0, days: 0, noService: 0 }
      for (const d of m.days) {
        const s = which === 'opt' ? d.opt : d.cur
        const basisNote0 = s?.unscorable
          ? `no timetable pattern for ${d.colour} — fixed cost only`
          : s?.noService
          ? `no standard services on like day ${s.basisDate ?? ''}`.trim()
          : s?.basisDate ? (which === 'cur' ? `actual · ${s.basisDate}` : `demand of ${s.basisDate}`) : (s ? 'day-type average (no like day)' : '')
        // A Firecon day (steam stood down, diesel only; sales % down on the like day) says so.
        const basisNote1 = s?.firecon ? `FIRECON day ${s.firecon.day} — steam stood down${s.firecon.pct > 0 ? `, sales −${s.firecon.pct}%` : ''}${basisNote0 ? ` · ${basisNote0}` : ''}` : basisNote0
        // The availability calendar: steam turns run diesel, turns with no engine, the engines out that day.
        const fl = s?.fleet
        const fleetNote = fl && (fl.swapped > 0 || fl.noEngine > 0 || fl.out.length > 0)
          ? [fl.noEngine > 0 ? `no engine for ${fl.noEngine} turn${fl.noEngine === 1 ? '' : 's'}` : '', fl.swapped > 0 ? `${fl.swapped} steam turn${fl.swapped === 1 ? '' : 's'} run diesel` : '', fl.out.length > 0 ? `out: ${fl.out.map((o) => `${o.label} (${o.reason})`).join(', ')}` : ''].filter(Boolean).join(' · ')
          : ''
        const basisNote2 = fleetNote ? `${fleetNote}${basisNote1 ? ` · ${basisNote1}` : ''}` : basisNote1
        const basisNote = s?.failure ? `FAILURE DAY — no engine of any traction for ${s.fleet?.noEngine ?? 1} turn${(s.fleet?.noEngine ?? 1) === 1 ? '' : 's'}; modelled as a non-running day${basisNote2 ? ` · ${basisNote2}` : ''}` : basisNote2
        const row = ws.addRow(s
          ? [d.date, DOWS[d.dow] ?? '', d.colour, s.services, s.sets ?? '', s.pax, s.revenue, s.costs, s.profit, d.pullman?.covers ?? 0, d.pullman?.net ?? 0, basisNote]
          : [d.date, DOWS[d.dow] ?? '', d.colour, '—', '—', '—', '—', '—', '—', d.pullman?.covers ?? 0, d.pullman?.net ?? 0, ''])
        fmt(row, [7, 8, 9, 11], [4, 5, 6, 10])
        row.getCell(12).font = { italic: true, size: 9, color: { argb: MUTED } }
        if (s?.noService) t.noService += 1
        if (s) {
          t.days += 1; t.services += s.services; t.pax += s.pax; t.revenue += s.revenue; t.costs += s.costs; t.profit += s.profit
        }
        t.pullman += d.pullman?.covers ?? 0
        t.pullmanNet += d.pullman?.net ?? 0
      }
      const trow = ws.addRow([`MONTH TOTAL (${t.days} days${t.noService > 0 ? `, ${t.noService} with no standard services` : ''})`, '', '', t.services, '', t.pax, t.revenue, t.costs, t.profit, t.pullman, t.pullmanNet, ''])
      fmt(trow, [7, 8, 9, 11], [4, 6, 10])
      boldTotal(trow)
      ws.addRow([])
    }
    daysBlock('opt', 'OPTIMISED — the designed timetable')
    daysBlock('cur', `UNOPTIMISED — as run today, actual sales from ${m.basis || 'the sales basis season'}`)
    ws.views = [{ state: 'frozen', ySplit: 2 }]
  }

  // ---- 3. One timetable sheet per month ------------------------------------
  for (const m of input.months) {
    if (m.plans.length === 0) continue
    const ws = wb.addWorksheet(`${m.label.slice(0, 27)} TT`.slice(0, 31))
    setCols(ws, [9, 16, 16, 9, 10, 10, 10, 10])
    titleRow(ws, `Timetables — ${m.label}`, 8)
    noteRow(ws, 'Every running pattern this month: the optimised (designed) timetable then the unoptimised (as run today), with capacity, from/to and times. ECS = empty stock positioning. The Pullman dining SERVICE of this pattern (one service = out and back, two legs) is listed on both sides — it traffics through the timetable and carries dining covers, not daily-services seats.', 8)
    for (const p of m.plans) {
      blockRow(ws, `${p.label} — runs ${p.dates.length} day${p.dates.length === 1 ? '' : 's'} (${p.dates[0] ?? ''}${p.dates.length > 1 ? ` to ${p.dates[p.dates.length - 1]}` : ''})`, 8)
      const listing = (rows: StudioXlsxTtRow[], label: string) => {
        const r = ws.addRow([label])
        ws.mergeCells(r.number, 1, r.number, 8)
        r.getCell(1).font = { bold: true, size: 9.5, color: { argb: MUTED } }
        headerRow(ws, ['Dep', 'From', 'To', 'Arr', 'Capacity', 'Set', 'Loco', 'Type'], 5)
        for (const w of rows) {
          const row = ws.addRow([w.time, w.from, w.to, w.arr, w.capacity, w.set, w.loco, w.kind])
          row.getCell(5).numFmt = INT
          if (w.kind !== 'Service') row.eachCell((c) => { c.font = { ...(c.font ?? {}), color: { argb: MUTED } } })
        }
        ws.addRow([])
      }
      listing(p.opt, 'Optimised (designed)')
      listing(p.cur, 'Unoptimised (as run today)')
    }
    ws.views = [{ state: 'frozen', ySplit: 2 }]
  }

  // ---- 4. Fleet: the register, the availability grid, the roster, the allocation ----
  const fl = input.fleet
  if (fl && fl.register.length > 0) {
    const ws = wb.addWorksheet('Fleet')
    const W = Math.max(24, fl.weeks.length + 2)
    setCols(ws, [22, ...Array.from({ length: W - 1 }, () => 11)])
    titleRow(ws, `Fleet — the rolling-stock register, availability and the season roster (${input.season})`, W)
    noteRow(ws, 'The register as the Cost model holds it (every field is the planner\'s). A chargeable mile is a TIMETABLED mile; NYMR pays maintenance, coal, oil and crew through the P&L lines, so a hire rate here is pure hire. Basis miles: the COO\'s figure when typed, else the contracted minimum is assumed, else estimated from the timetable. Availability: an engine is out for its washout WEEK (every cycle either side of the washout date — cooling down, washing out, firing up), its dated out-of-service periods, an expired boiler certificate, or outside its active dates. The roster decides which engine works which turn over the whole season: the planner\'s pins; paid-for contract miles first, spread over the engine\'s available running days and run in blocks of about a week, the minimum-contract engines interleaving; then owned steam; then the cheapest contract steam for the day; then diesel (swapped). A FAILURE DAY has no engine of any traction for a turn and is modelled as a non-running day.', W)
    blockRow(ws, 'THE REGISTER', W)
    headerRow(ws, ['Engine', 'Name', 'Owner', 'NYMR / contract', 'Traction', 'Fuel', 'Whitby enabled', 'In simulation', '£ per mile', '£ per day', 'Fixed £ a year', 'Maintenance £ per mile', 'Minimum miles', 'Basis miles', 'Basis miles assumed', 'Active from', 'Active to', 'Washout date', 'Washout cycle (days)', 'Washout lock-out (days)', 'Boiler certificate', 'Out of service', 'Notes'], 9)
    for (const r of fl.register) {
      const row = ws.addRow([r.number, r.name, r.owner, r.ownership === 'nymr' ? 'NYMR' : 'Contract', r.traction, r.fuel, r.whitby ? 'yes' : '', r.inSimulation ? 'yes' : 'no',
        r.costPerMile ?? '', r.dayRate ?? '', r.fixedAnnualCost ?? '', r.maintenancePerMile ?? '', r.minContractMiles ?? '', r.basisMiles ?? '', r.basisAssumed,
        r.activeFrom ?? '', r.activeTo ?? '', r.washoutSeed ?? '', r.washoutCycleDays ?? '', r.washoutLockoutDays ?? '', r.boilerCertExpiry ?? '', r.unavailable, r.notes])
      for (const c of [9, 10, 11, 12]) if (typeof row.getCell(c).value === 'number') row.getCell(c).numFmt = '£#,##0.00'
      for (const c of [13, 14]) if (typeof row.getCell(c).value === 'number') row.getCell(c).numFmt = INT
      if (!r.inSimulation) row.eachCell((c) => { c.font = { ...(c.font ?? {}), color: { argb: MUTED } } })
    }
    ws.addRow([])
    if (fl.weeks.length > 0) {
      blockRow(ws, 'AVAILABILITY — days OUT per engine per running week (week commencing); beneath, the fewest engines of each traction available on any running day of the week', W)
      headerRow(ws, ['Engine', ...fl.weeks], 2)
      for (const r of fl.outDays) {
        const row = ws.addRow([r.engine, ...r.cells.map((c) => (c > 0 ? c : ''))])
        r.cells.forEach((c, i) => { if (c > 0) { const cell = row.getCell(i + 2); cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3C8C1' } }; cell.note = r.reasons[i].join('; ') } })
      }
      for (const a of fl.available) {
        const row = ws.addRow([`${a.traction} available (fewest)`, ...a.cells])
        boldTotal(row)
      }
      ws.addRow([])
    }
    if (fl.engines && fl.engines.length > 0) {
      blockRow(ws, 'EACH ENGINE OVER THE SEASON — the roster', W)
      const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      headerRow(ws, ['Engine', 'Traction', 'Contracted minimum', 'Miles rostered', 'Of minimum', 'Days worked', 'Blocks', ...fl.monthNos.map((m) => `${MON[m - 1]} miles`)], 3)
      for (const e of fl.engines) {
        const row = ws.addRow([e.engine, e.traction, e.minimum ?? '', Math.round(e.miles), e.minimum ? Math.round((e.miles / e.minimum) * 100) / 100 : '', e.days, e.blocks, ...fl.monthNos.map((m) => Math.round(e.milesByMonth[m] ?? 0))])
        fmt(row, [], [3, 4, 6, 7, ...fl.monthNos.map((_, i) => 8 + i)])
        if (e.minimum) row.getCell(5).numFmt = '0%'
      }
      ws.addRow([])
    }
    if (fl.allocation.length > 0) {
      blockRow(ws, 'ALLOCATION — every date, every turn, the engine it got and why', W)
      headerRow(ws, ['Date', 'Colour', 'Turn', 'Traction', 'Engine', 'Why', 'Timetabled miles', 'Steam turn run diesel', 'Pinned', 'FAILURE DAY'], 7)
      for (const a of fl.allocation) {
        const row = ws.addRow([a.date, a.colour, a.turn, a.traction, a.engine, a.reason, Math.round(a.miles * 10) / 10, a.swapped ? 'yes' : '', a.pinned ? 'yes' : '', a.failure ? 'FAILURE — non-running' : ''])
        if (a.failure) row.eachCell((c) => { c.font = { ...(c.font ?? {}), bold: true, color: { argb: 'FFB83A2B' } } })
        else if (a.swapped) row.getCell(8).font = { bold: true, color: { argb: 'FFB0791F' } }
      }
    }
    ws.views = [{ state: 'frozen', ySplit: 2 }]
  }

  // ---- 5. Cost model: the P&L lines, source and modelled by month --------------------
  const cm = input.costModel
  if (cm && cm.lines.length > 0) {
    const ws = wb.addWorksheet('Cost model')
    setCols(ws, [46, 18, 12, 22, ...Array.from({ length: 13 }, () => 12), 12])
    titleRow(ws, `Cost model — the ${cm.basis} Management P&L, source and modelled (${input.season})`, 18)
    noteRow(ws, 'The same lines as the Cost model stage. FIXED lines are charged to their own month; VARIABLE lines are re-priced at the basis rate × the designed timetable\'s activity. Under Hire of Rolling Stock: the memo (the P&L line before deductions, not in the totals), each register engine\'s contracted commitment (fixed) and hire on timetabled miles and days (variable, by contract on the design), the planner\'s Fixed Rolling Stock spread, and Variable after Fixed — what remains. The planner\'s own fixed lines under other P&L lines appear the same way, each deducted from its line.', 18)
    const MONTHS = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb']
    const table = (which: 'source' | 'modelled', title: string) => {
      blockRow(ws, title, 18)
      headerRow(ws, ['Line', 'Category', 'Behaviour', 'Rate', ...MONTHS, 'Season', which === 'modelled' ? 'Change' : ''], 5)
      let tot = Array(12).fill(0) as number[]
      for (const l of cm.lines) {
        const months = which === 'source' ? l.basisMonths : l.modelMonths
        const season = which === 'source' ? l.basisTotal : l.modelTotal
        const row = ws.addRow([`${l.label}${l.memo ? ' (memo — not in totals)' : ''}`, l.category, l.behaviour, l.rate ?? '', ...months, season, which === 'modelled' ? l.delta : ''])
        fmt(row, [...MONTHS.map((_, i) => 5 + i), 17, 18], [])
        if (!l.included) row.eachCell((c) => { c.font = { ...(c.font ?? {}), color: { argb: MUTED }, italic: true } })
        if (l.rollingStock) row.getCell(1).font = { ...(row.getCell(1).font ?? {}), bold: !l.memo, color: { argb: l.memo ? MUTED : 'FF0B3D2E' } }
        if (l.included) tot = tot.map((t, i) => t + (months[i] ?? 0))
      }
      const trow = ws.addRow(['TOTAL (included lines)', '', '', '', ...tot, tot.reduce((a, b) => a + b, 0), ''])
      fmt(trow, [...MONTHS.map((_, i) => 5 + i), 17], [])
      boldTotal(trow)
      ws.addRow([])
    }
    table('source', `1 · MODELLING SOURCE — actual ${cm.basis} P&L`)
    table('modelled', cm.hasModel ? '2 · AFTER THE BUILD — modelled costs and the change' : '2 · AFTER THE BUILD — the season is not built; the modelled column mirrors the source')
    ws.views = [{ state: 'frozen', ySplit: 2 }]
  }

  return wb
}

/** Compose and download — separated so the composition can be tested headlessly. */
export async function buildStudioWorkbook(input: StudioXlsxInput): Promise<void> {
  const wb = await composeStudioWorkbook(input)
  const buf = await wb.xlsx.writeBuffer()
  const stamp = new Date().toISOString().slice(0, 10)
  downloadBlob(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `timetable-studio-${input.season.replace('/', '-')}-${stamp}.xlsx`,
  )
}
